import React, { useState, useEffect, useCallback } from "react";
import PortalAdminLayout from "./PortalAdminLayout";
import "./PortalDashboard.css";

const MarketingDashboard = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [smsCredits, setSmsCredits] = useState(null);
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState("campaigns");
  const [showCampaignForm, setShowCampaignForm] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    type: "email",
    subject: "",
    content: "",
    audienceType: "all_customers",
    audienceFilter: {},
    scheduledFor: "",
  });

  const [templateFormData, setTemplateFormData] = useState({
    name: "",
    type: "email",
    subject: "",
    content: "",
  });

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      window.location.href = "/login";
      return {};
    }
    return { Authorization: `Bearer ${token}` };
  }, []);

  const getApiBaseUrl = useCallback(() => {
    const url = process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
    if (!url || !url.startsWith("http")) {
      return "https://api.ogestyle.com";
    }
    return url;
  }, []);

  useEffect(() => {
    const checkPortalAdmin = () => {
      const token = localStorage.getItem("ogestyle_token");
      if (!token) {
        window.location.href = "/login";
        return false;
      }
      return true;
    };

    if (checkPortalAdmin()) {
      fetchCampaigns();
      fetchTemplates();
      fetchSmsCredits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (formData.audienceType && showCampaignForm) {
      fetchAudiencePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.audienceType, formData.audienceFilter, showCampaignForm]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      if (!headers.Authorization) return;

      const res = await fetch(`${getApiBaseUrl()}/api/marketing/campaigns`, {
        headers,
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch campaigns: ${res.status}`);
      }

      const data = await res.json();
      setCampaigns(data.campaigns || []);
    } catch (err) {
      setError(err.message || "Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers.Authorization) return;

      const res = await fetch(`${getApiBaseUrl()}/api/marketing/templates`, {
        headers,
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch templates: ${res.status}`);
      }

      const data = await res.json();
      setTemplates(data.templates || []);
    } catch (err) {
    }
  };

  const fetchSmsCredits = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers.Authorization) return;

      const res = await fetch(`${getApiBaseUrl()}/api/marketing/sms-credits`, {
        headers,
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch SMS credits: ${res.status}`);
      }

      const data = await res.json();
      setSmsCredits(data.credits);
    } catch (err) {
    }
  };

  const fetchAudiencePreview = async () => {
    try {
      setPreviewLoading(true);
      const headers = getAuthHeaders();
      if (!headers.Authorization) return;

      const queryParams = new URLSearchParams({
        audienceType: formData.audienceType,
      });

      const res = await fetch(
        `${getApiBaseUrl()}/api/marketing/audience-preview?${queryParams}`,
        {
          headers,
        },
      );

      if (!res.ok) {
        throw new Error(`Failed to fetch audience preview: ${res.status}`);
      }

      const data = await res.json();
      setAudiencePreview(data.preview);
    } catch (err) {
      setAudiencePreview({ count: 0 });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const headers = {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      };
      if (!headers.Authorization) return;

      const payload = {
        ...formData,
        scheduledFor: formData.scheduledFor || undefined,
      };

      const res = await fetch(`${getApiBaseUrl()}/api/marketing/campaigns`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.error || `Failed to create campaign: ${res.status}`,
        );
      }

      const data = await res.json();
      setCampaigns((prev) => [data.campaign, ...prev]);
      setShowCampaignForm(false);
      setFormData({
        name: "",
        type: "email",
        subject: "",
        content: "",
        audienceType: "all_customers",
        audienceFilter: {},
        scheduledFor: "",
      });
      setAudiencePreview(null);
      setSuccess("Campaign created successfully!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || "Failed to create campaign");
    }
  };

  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm("Are you sure you want to send this campaign now?")) {
      return;
    }

    try {
      const headers = {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      };
      if (!headers.Authorization) return;

      const res = await fetch(
        `${getApiBaseUrl()}/api/marketing/campaigns/${campaignId}/send`,
        {
          method: "POST",
          headers,
        },
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.error || `Failed to send campaign: ${res.status}`,
        );
      }

      setSuccess("Campaign sent successfully!");
      setTimeout(() => setSuccess(null), 3000);
      fetchCampaigns(); // Refresh list
    } catch (err) {
      setError(err.message || "Failed to send campaign");
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const headers = {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      };
      if (!headers.Authorization) return;

      const res = await fetch(`${getApiBaseUrl()}/api/marketing/templates`, {
        method: "POST",
        headers,
        body: JSON.stringify(templateFormData),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.error || `Failed to create template: ${res.status}`,
        );
      }

      const data = await res.json();
      setTemplates((prev) => [data.template, ...prev]);
      setShowTemplateForm(false);
      setTemplateFormData({
        name: "",
        type: "email",
        subject: "",
        content: "",
      });
      setSuccess("Template created successfully!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || "Failed to create template");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "sent":
        return { bg: "#d1fae5", color: "#065f46", border: "#10b981" };
      case "scheduled":
        return { bg: "#fef3c7", color: "#92400e", border: "#f59e0b" };
      case "draft":
        return { bg: "#e5e7eb", color: "#374151", border: "#9ca3af" };
      case "sending":
        return { bg: "#dbeafe", color: "#1e40af", border: "#3b82f6" };
      default:
        return { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
    }
  };

  const renderCampaignsList = () => (
    <div style={{ marginTop: "20px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <h2
          style={{
            fontSize: "20px",
            fontWeight: 700,
            color: "#111827",
            margin: 0,
          }}
        >
          📧 Email & SMS Campaigns
        </h2>
        <button
          onClick={() => setShowCampaignForm(true)}
          style={{
            padding: "10px 20px",
            backgroundColor: "#6366f1",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          + Create Campaign
        </button>
      </div>

      {/* Stats Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            border: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <span style={{ fontSize: "20px", marginRight: "8px" }}>📊</span>
            <span
              style={{
                fontSize: "13px",
                color: "#6b7280",
                fontWeight: 600,
              }}
            >
              Total Campaigns
            </span>
          </div>
          <div style={{ fontSize: "32px", fontWeight: 700, color: "#6366f1" }}>
            {campaigns.length}
          </div>
        </div>
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            border: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <span style={{ fontSize: "20px", marginRight: "8px" }}>✅</span>
            <span
              style={{
                fontSize: "13px",
                color: "#6b7280",
                fontWeight: 600,
              }}
            >
              Sent
            </span>
          </div>
          <div style={{ fontSize: "32px", fontWeight: 700, color: "#10b981" }}>
            {campaigns.filter((c) => c.status === "sent").length}
          </div>
        </div>
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            border: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <span style={{ fontSize: "20px", marginRight: "8px" }}>⏰</span>
            <span
              style={{
                fontSize: "13px",
                color: "#6b7280",
                fontWeight: 600,
              }}
            >
              Scheduled
            </span>
          </div>
          <div style={{ fontSize: "32px", fontWeight: 700, color: "#f59e0b" }}>
            {campaigns.filter((c) => c.status === "scheduled").length}
          </div>
        </div>
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            border: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <span style={{ fontSize: "20px", marginRight: "8px" }}>📝</span>
            <span
              style={{
                fontSize: "13px",
                color: "#6b7280",
                fontWeight: 600,
              }}
            >
              Drafts
            </span>
          </div>
          <div style={{ fontSize: "32px", fontWeight: 700, color: "#9ca3af" }}>
            {campaigns.filter((c) => c.status === "draft").length}
          </div>
        </div>
      </div>

      {campaigns.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            backgroundColor: "#f9fafb",
            borderRadius: "12px",
            border: "2px dashed #d1d5db",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>📧</div>
          <div
            style={{ fontSize: "16px", color: "#6b7280", marginBottom: "8px" }}
          >
            No campaigns yet
          </div>
          <div style={{ fontSize: "14px", color: "#9ca3af" }}>
            Create your first marketing campaign to reach your audience!
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: "20px",
          }}
        >
          {campaigns.map((campaign) => {
            const statusStyle = getStatusColor(campaign.status);
            return (
              <div
                key={campaign._id}
                style={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  padding: "20px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #e5e7eb",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "12px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: 700,
                      color: "#111827",
                      margin: 0,
                      flex: 1,
                    }}
                  >
                    {campaign.name}
                  </h3>
                  <span
                    style={{
                      padding: "4px 12px",
                      backgroundColor: statusStyle.bg,
                      color: statusStyle.color,
                      border: `1px solid ${statusStyle.border}`,
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {campaign.status}
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    fontSize: "14px",
                    color: "#6b7280",
                  }}
                >
                  <div>
                    <strong style={{ color: "#374151" }}>Type:</strong>{" "}
                    {campaign.type === "email" ? "📧 Email" : "💬 SMS"}
                  </div>
                  <div>
                    <strong style={{ color: "#374151" }}>Audience:</strong>{" "}
                    {campaign.audienceType.replace(/_/g, " ")}
                  </div>
                  {campaign.type === "email" && campaign.subject && (
                    <div>
                      <strong style={{ color: "#374151" }}>Subject:</strong>{" "}
                      {campaign.subject}
                    </div>
                  )}
                  {campaign.scheduledFor && (
                    <div>
                      <strong style={{ color: "#374151" }}>Scheduled:</strong>{" "}
                      {new Date(campaign.scheduledFor).toLocaleString()}
                    </div>
                  )}
                  {campaign.sentAt && (
                    <div>
                      <strong style={{ color: "#374151" }}>Sent:</strong>{" "}
                      {new Date(campaign.sentAt).toLocaleString()}
                    </div>
                  )}
                </div>

                {campaign.analytics && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(80px, 1fr))",
                      gap: "12px",
                      padding: "12px",
                      backgroundColor: "#f9fafb",
                      borderRadius: "8px",
                      marginTop: "8px",
                    }}
                  >
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: 700,
                          color: "#111827",
                        }}
                      >
                        {campaign.analytics.totalSent || 0}
                      </div>
                      <div style={{ fontSize: "12px", color: "#6b7280" }}>
                        Sent
                      </div>
                    </div>
                    {campaign.type === "email" && (
                      <>
                        <div style={{ textAlign: "center" }}>
                          <div
                            style={{
                              fontSize: "20px",
                              fontWeight: 700,
                              color: "#10b981",
                            }}
                          >
                            {campaign.analytics.opens || 0}
                          </div>
                          <div style={{ fontSize: "12px", color: "#6b7280" }}>
                            Opens
                          </div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div
                            style={{
                              fontSize: "20px",
                              fontWeight: 700,
                              color: "#6366f1",
                            }}
                          >
                            {campaign.analytics.clicks || 0}
                          </div>
                          <div style={{ fontSize: "12px", color: "#6b7280" }}>
                            Clicks
                          </div>
                        </div>
                      </>
                    )}
                    {campaign.type === "sms" && (
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: "20px",
                            fontWeight: 700,
                            color: "#f59e0b",
                          }}
                        >
                          {campaign.smsCreditsUsed || 0}
                        </div>
                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                          Credits
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {campaign.status === "draft" && (
                  <button
                    onClick={() => handleSendCampaign(campaign._id)}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#10b981",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: "pointer",
                      marginTop: "8px",
                    }}
                  >
                    Send Now
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderTemplates = () => (
    <div style={{ marginTop: "20px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <h2
          style={{
            fontSize: "20px",
            fontWeight: 700,
            color: "#111827",
            margin: 0,
          }}
        >
          📝 Campaign Templates
        </h2>
        <button
          onClick={() => setShowTemplateForm(true)}
          style={{
            padding: "10px 20px",
            backgroundColor: "#6366f1",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          + Create Template
        </button>
      </div>

      {templates.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            backgroundColor: "#f9fafb",
            borderRadius: "12px",
            border: "2px dashed #d1d5db",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>📝</div>
          <div
            style={{ fontSize: "16px", color: "#6b7280", marginBottom: "8px" }}
          >
            No templates yet
          </div>
          <div style={{ fontSize: "14px", color: "#9ca3af" }}>
            Create reusable templates for faster campaign creation!
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: "20px",
          }}
        >
          {templates.map((template) => (
            <div
              key={template._id}
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "20px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                border: "1px solid #e5e7eb",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "12px",
                }}
              >
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "#111827",
                    margin: 0,
                  }}
                >
                  {template.name}
                </h3>
                <span
                  style={{
                    padding: "4px 12px",
                    backgroundColor:
                      template.type === "email" ? "#dbeafe" : "#fef3c7",
                    color: template.type === "email" ? "#1e40af" : "#92400e",
                    border: `1px solid ${template.type === "email" ? "#3b82f6" : "#f59e0b"}`,
                    borderRadius: "12px",
                    fontSize: "11px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                  }}
                >
                  {template.type}
                </span>
              </div>
              {template.type === "email" && template.subject && (
                <div
                  style={{
                    fontSize: "14px",
                    color: "#374151",
                    marginBottom: "12px",
                  }}
                >
                  <strong>Subject:</strong> {template.subject}
                </div>
              )}
              <div
                style={{
                  fontSize: "13px",
                  color: "#6b7280",
                  lineHeight: "1.6",
                  maxHeight: "80px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {template.content.substring(0, 150)}...
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderSmsCredits = () => (
    <div style={{ marginTop: "20px" }}>
      <h2
        style={{
          fontSize: "20px",
          fontWeight: 700,
          color: "#111827",
          marginBottom: "24px",
        }}
      >
        💬 SMS Credits
      </h2>

      {smsCredits ? (
        <div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "20px",
              marginBottom: "32px",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "24px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                border: "1px solid #e5e7eb",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "12px",
                }}
              >
                <span style={{ fontSize: "20px", marginRight: "8px" }}>📅</span>
                <span
                  style={{
                    fontSize: "13px",
                    color: "#6b7280",
                    fontWeight: 600,
                  }}
                >
                  Monthly Allowance
                </span>
              </div>
              <div
                style={{
                  fontSize: "36px",
                  fontWeight: 700,
                  color: "#6366f1",
                  marginBottom: "12px",
                }}
              >
                {smsCredits.monthlyAllowance}
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: "#374151",
                  marginBottom: "8px",
                }}
              >
                Used: <strong>{smsCredits.monthlyUsed}</strong> | Remaining:{" "}
                <strong style={{ color: "#10b981" }}>
                  {smsCredits.monthlyAllowance - smsCredits.monthlyUsed}
                </strong>
              </div>
              <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                Resets:{" "}
                {new Date(smsCredits.nextResetDate).toLocaleDateString()}
              </div>
            </div>

            <div
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "24px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                border: "1px solid #e5e7eb",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "12px",
                }}
              >
                <span style={{ fontSize: "20px", marginRight: "8px" }}>💳</span>
                <span
                  style={{
                    fontSize: "13px",
                    color: "#6b7280",
                    fontWeight: 600,
                  }}
                >
                  Purchased Credits
                </span>
              </div>
              <div
                style={{
                  fontSize: "36px",
                  fontWeight: 700,
                  color: "#f59e0b",
                  marginBottom: "12px",
                }}
              >
                {smsCredits.purchasedCredits}
              </div>
              <div style={{ fontSize: "14px", color: "#374151" }}>
                Total Purchased: <strong>{smsCredits.totalPurchased}</strong>
              </div>
            </div>

            <div
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "24px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                border: "2px solid #10b981",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "12px",
                }}
              >
                <span style={{ fontSize: "20px", marginRight: "8px" }}>💬</span>
                <span
                  style={{
                    fontSize: "13px",
                    color: "#6b7280",
                    fontWeight: 600,
                  }}
                >
                  Total Available
                </span>
              </div>
              <div
                style={{
                  fontSize: "36px",
                  fontWeight: 700,
                  color: "#10b981",
                  marginBottom: "12px",
                }}
              >
                {smsCredits.monthlyAllowance -
                  smsCredits.monthlyUsed +
                  smsCredits.purchasedCredits}
              </div>
              <div
                style={{ fontSize: "13px", color: "#059669", fontWeight: 600 }}
              >
                Ready to use
              </div>
            </div>
          </div>

          {smsCredits.purchaseHistory &&
            smsCredits.purchaseHistory.length > 0 && (
              <div
                style={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  padding: "24px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #e5e7eb",
                  marginBottom: "24px",
                }}
              >
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "#111827",
                    marginBottom: "16px",
                  }}
                >
                  Purchase History
                </h3>
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "14px",
                    }}
                  >
                    <thead>
                      <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "left",
                            fontWeight: 600,
                            color: "#6b7280",
                          }}
                        >
                          Date
                        </th>
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "right",
                            fontWeight: 600,
                            color: "#6b7280",
                          }}
                        >
                          Credits
                        </th>
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "right",
                            fontWeight: 600,
                            color: "#6b7280",
                          }}
                        >
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {smsCredits.purchaseHistory.map((purchase, idx) => (
                        <tr
                          key={idx}
                          style={{
                            borderBottom: "1px solid #f3f4f6",
                            backgroundColor:
                              idx % 2 === 0 ? "white" : "#f9fafb",
                          }}
                        >
                          <td style={{ padding: "12px", color: "#374151" }}>
                            {new Date(purchase.date).toLocaleDateString()}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              textAlign: "right",
                              color: "#374151",
                              fontWeight: 600,
                            }}
                          >
                            {purchase.credits}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              textAlign: "right",
                              color: "#10b981",
                              fontWeight: 700,
                            }}
                          >
                            ${purchase.amount.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          <div
            style={{
              backgroundColor: "#f9fafb",
              borderRadius: "12px",
              padding: "24px",
              border: "1px solid #e5e7eb",
              textAlign: "center",
            }}
          >
            <button
              style={{
                padding: "12px 24px",
                backgroundColor: "#6366f1",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
                marginBottom: "8px",
              }}
            >
              Purchase More Credits
            </button>
            <div style={{ fontSize: "13px", color: "#6b7280" }}>
              Contact support to purchase additional SMS credits
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            fontSize: "14px",
            color: "#6b7280",
          }}
        >
          Loading SMS credits...
        </div>
      )}
    </div>
  );

  const renderCampaignForm = () => (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px",
      }}
      onClick={() => setShowCampaignForm(false)}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          maxWidth: "700px",
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: "24px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2
            style={{
              fontSize: "20px",
              fontWeight: 700,
              color: "#111827",
              margin: 0,
            }}
          >
            Create Marketing Campaign
          </h2>
          <button
            onClick={() => setShowCampaignForm(false)}
            style={{
              fontSize: "28px",
              color: "#9ca3af",
              background: "none",
              border: "none",
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleCreateCampaign} style={{ padding: "24px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "16px",
              marginBottom: "16px",
            }}
          >
            <div>
              <label
                htmlFor="name"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#374151",
                  marginBottom: "6px",
                }}
              >
                Campaign Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="e.g., Summer Sale 2025"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div>
              <label
                htmlFor="type"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#374151",
                  marginBottom: "6px",
                }}
              >
                Campaign Type *
              </label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                  backgroundColor: "white",
                  cursor: "pointer",
                }}
              >
                <option value="email">Email</option>
                <option value="sms">SMS</option>
              </select>
            </div>
          </div>

          {formData.type === "email" && (
            <div style={{ marginBottom: "16px" }}>
              <label
                htmlFor="subject"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#374151",
                  marginBottom: "6px",
                }}
              >
                Email Subject *
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required={formData.type === "email"}
                placeholder="Your subject line here"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>
          )}

          <div style={{ marginBottom: "16px" }}>
            <label
              htmlFor="content"
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: 600,
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              {formData.type === "email" ? "Email Body" : "SMS Message"} *
            </label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              required
              rows={formData.type === "email" ? 10 : 4}
              placeholder={
                formData.type === "email"
                  ? "Write your email content here..."
                  : "Keep it short (160 characters max recommended)"
              }
              maxLength={formData.type === "sms" ? 160 : undefined}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
                boxSizing: "border-box",
                fontFamily: "inherit",
                resize: "vertical",
              }}
            />
            {formData.type === "sms" && (
              <div
                style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}
              >
                {formData.content.length}/160 characters
              </div>
            )}
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label
              htmlFor="audienceType"
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: 600,
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              Target Audience *
            </label>
            <select
              id="audienceType"
              name="audienceType"
              value={formData.audienceType}
              onChange={handleInputChange}
              required
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
                boxSizing: "border-box",
                backgroundColor: "white",
                cursor: "pointer",
              }}
            >
              <option value="all_customers">All Customers</option>
              <option value="guest_leads">
                Guest Leads (Newsletter Subscribers)
              </option>
              <option value="active_customers">
                Active Customers (Last 90 days)
              </option>
              <option value="inactive_customers">
                Inactive Customers (No activity 90+ days)
              </option>
              <option value="high_value_customers">High Value Customers</option>
              <option value="cart_abandoned">Cart Abandoned</option>
            </select>
          </div>

          {audiencePreview && !previewLoading && (
            <div
              style={{
                padding: "16px",
                backgroundColor: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#166534",
                  marginBottom: "8px",
                }}
              >
                📊 Audience Preview
              </div>
              <div style={{ fontSize: "14px", color: "#166534" }}>
                <strong>Estimated Recipients:</strong>{" "}
                {audiencePreview.count || 0}
              </div>
              {formData.type === "sms" && smsCredits && (
                <div
                  style={{
                    fontSize: "13px",
                    color: "#166534",
                    marginTop: "4px",
                  }}
                >
                  <strong>SMS Credits Required:</strong>{" "}
                  {audiencePreview.count || 0}
                  <br />
                  <strong>Available Credits:</strong>{" "}
                  {smsCredits.monthlyAllowance -
                    smsCredits.monthlyUsed +
                    smsCredits.purchasedCredits}
                  {audiencePreview.count >
                    smsCredits.monthlyAllowance -
                      smsCredits.monthlyUsed +
                      smsCredits.purchasedCredits && (
                    <span style={{ color: "#dc2626", fontWeight: 700 }}>
                      {" "}
                      (Insufficient credits!)
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          <div style={{ marginBottom: "24px" }}>
            <label
              htmlFor="scheduledFor"
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: 600,
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              Schedule Send (Optional)
            </label>
            <input
              type="datetime-local"
              id="scheduledFor"
              name="scheduledFor"
              value={formData.scheduledFor}
              onChange={handleInputChange}
              min={new Date().toISOString().slice(0, 16)}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
            <div
              style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}
            >
              Leave empty to save as draft
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              onClick={() => setShowCampaignForm(false)}
              style={{
                padding: "10px 20px",
                backgroundColor: "#f3f4f6",
                color: "#374151",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: "10px 20px",
                backgroundColor: "#6366f1",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {formData.scheduledFor ? "Schedule Campaign" : "Save as Draft"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderTemplateForm = () => (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px",
      }}
      onClick={() => setShowTemplateForm(false)}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          maxWidth: "700px",
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: "24px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2
            style={{
              fontSize: "20px",
              fontWeight: 700,
              color: "#111827",
              margin: 0,
            }}
          >
            Create Campaign Template
          </h2>
          <button
            onClick={() => setShowTemplateForm(false)}
            style={{
              fontSize: "28px",
              color: "#9ca3af",
              background: "none",
              border: "none",
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleCreateTemplate} style={{ padding: "24px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "16px",
              marginBottom: "16px",
            }}
          >
            <div>
              <label
                htmlFor="template-name"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#374151",
                  marginBottom: "6px",
                }}
              >
                Template Name *
              </label>
              <input
                type="text"
                id="template-name"
                value={templateFormData.name}
                onChange={(e) =>
                  setTemplateFormData((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                required
                placeholder="e.g., Welcome Email"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div>
              <label
                htmlFor="template-type"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#374151",
                  marginBottom: "6px",
                }}
              >
                Template Type *
              </label>
              <select
                id="template-type"
                value={templateFormData.type}
                onChange={(e) =>
                  setTemplateFormData((prev) => ({
                    ...prev,
                    type: e.target.value,
                  }))
                }
                required
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                  backgroundColor: "white",
                  cursor: "pointer",
                }}
              >
                <option value="email">Email</option>
                <option value="sms">SMS</option>
              </select>
            </div>
          </div>

          {templateFormData.type === "email" && (
            <div style={{ marginBottom: "16px" }}>
              <label
                htmlFor="template-subject"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#374151",
                  marginBottom: "6px",
                }}
              >
                Email Subject *
              </label>
              <input
                type="text"
                id="template-subject"
                value={templateFormData.subject}
                onChange={(e) =>
                  setTemplateFormData((prev) => ({
                    ...prev,
                    subject: e.target.value,
                  }))
                }
                required={templateFormData.type === "email"}
                placeholder="Subject line template"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>
          )}

          <div style={{ marginBottom: "24px" }}>
            <label
              htmlFor="template-content"
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: 600,
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              {templateFormData.type === "email"
                ? "Email Content"
                : "SMS Message"}{" "}
              *
            </label>
            <textarea
              id="template-content"
              value={templateFormData.content}
              onChange={(e) =>
                setTemplateFormData((prev) => ({
                  ...prev,
                  content: e.target.value,
                }))
              }
              required
              rows={templateFormData.type === "email" ? 12 : 4}
              placeholder={
                templateFormData.type === "email"
                  ? "Write your template content here...\n\nTip: Use {{firstName}}, {{businessName}} for personalization"
                  : "SMS template (160 characters max recommended)"
              }
              maxLength={templateFormData.type === "sms" ? 160 : undefined}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
                boxSizing: "border-box",
                fontFamily: "inherit",
                resize: "vertical",
              }}
            />
            {templateFormData.type === "sms" && (
              <div
                style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}
              >
                {templateFormData.content.length}/160 characters
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              onClick={() => setShowTemplateForm(false)}
              style={{
                padding: "10px 20px",
                backgroundColor: "#f3f4f6",
                color: "#374151",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: "10px 20px",
                backgroundColor: "#6366f1",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Create Template
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  if (loading) {
    return (
      <PortalAdminLayout>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            fontSize: "16px",
            color: "#6b7280",
          }}
        >
          Loading marketing dashboard...
        </div>
      </PortalAdminLayout>
    );
  }

  return (
    <PortalAdminLayout>
      <div style={{ padding: "20px", maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: "32px" }}>
          <h1
            style={{
              fontSize: "28px",
              fontWeight: 700,
              color: "#111827",
              marginBottom: "8px",
            }}
          >
            📢 Marketing & Campaigns
          </h1>
          <p style={{ fontSize: "14px", color: "#6b7280", margin: 0 }}>
            Manage email and SMS campaigns, track engagement, and monitor SMS
            credits
          </p>
        </div>

        {/* Alerts */}
        {error && (
          <div
            style={{
              padding: "16px 20px",
              backgroundColor: "#fee",
              border: "1px solid #fcc",
              borderRadius: "8px",
              color: "#c33",
              fontSize: "14px",
              marginBottom: "24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              style={{
                background: "none",
                border: "none",
                fontSize: "20px",
                color: "#c33",
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>
        )}

        {success && (
          <div
            style={{
              padding: "16px 20px",
              backgroundColor: "#f0fdf4",
              border: "1px solid #86efac",
              borderRadius: "8px",
              color: "#166534",
              fontSize: "14px",
              marginBottom: "24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{success}</span>
            <button
              onClick={() => setSuccess(null)}
              style={{
                background: "none",
                border: "none",
                fontSize: "20px",
                color: "#166534",
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            borderBottom: "2px solid #e5e7eb",
            marginBottom: "8px",
          }}
        >
          {[
            { key: "campaigns", label: "Campaigns", emoji: "📧" },
            { key: "templates", label: "Templates", emoji: "📝" },
            { key: "credits", label: "SMS Credits", emoji: "💬" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: "12px 24px",
                border: "none",
                borderBottom:
                  activeTab === tab.key
                    ? "3px solid #6366f1"
                    : "3px solid transparent",
                backgroundColor: "transparent",
                color: activeTab === tab.key ? "#6366f1" : "#6b7280",
                fontSize: "14px",
                fontWeight: activeTab === tab.key ? 700 : 600,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              {tab.emoji} {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "campaigns" && renderCampaignsList()}
        {activeTab === "templates" && renderTemplates()}
        {activeTab === "credits" && renderSmsCredits()}

        {/* Modals */}
        {showCampaignForm && renderCampaignForm()}
        {showTemplateForm && renderTemplateForm()}
      </div>
    </PortalAdminLayout>
  );
};

export default MarketingDashboard;
